/**
 * FitnessStoreManagement — متجر 606 Fitness
 * Add, count, edit, search and track products inside the 606 Fitness store.
 * Uses local optimistic state; backend endpoint /api/fitness/products is used
 * if available, otherwise UI works in-memory so admin can prototype quickly.
 */
import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import {
  ShoppingBag, Loader2, Search, RefreshCw, Plus, Edit, Trash2,
  Package, DollarSign, X, Save, Boxes, CheckCircle2, AlertCircle,
} from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import { useLanguage } from '../context/LanguageContext';
import { getAuthHeaders } from '../utils/authHeaders';
import { floorAED } from '../utils/money';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EMPTY_FORM = {
  id: null,
  name_ar: '',
  name_en: '',
  category: 'supplements',
  price: '',
  stock: '',
  status: 'active',
  description: '',
};

const CATEGORIES = [
  { value: 'supplements', ar: 'مكملات غذائية',  en: 'Supplements' },
  { value: 'equipment',   ar: 'معدات تدريب',    en: 'Equipment' },
  { value: 'apparel',     ar: 'ملابس رياضية',   en: 'Apparel' },
  { value: 'nutrition',   ar: 'وجبات صحية',     en: 'Nutrition' },
  { value: 'accessories', ar: 'اكسسوارات',      en: 'Accessories' },
];

export default function FitnessStoreManagement() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('all');
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/fitness/products`, { headers: getAuthHeaders() });
      setProducts(res.data?.data || res.data?.products || []);
    } catch (err) {
      // graceful empty state when endpoint is not ready yet
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const visible = products.filter((p) => {
    if (catFilter !== 'all' && p.category !== catFilter) return false;
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return [p.name_ar, p.name_en, p.description].some((v) => v && String(v).toLowerCase().includes(q));
  });

  const stats = {
    total: products.length,
    active: products.filter((p) => p.status === 'active').length,
    out_of_stock: products.filter((p) => Number(p.stock) <= 0).length,
    inventory_value: products.reduce((s, p) => s + (Number(p.price) || 0) * (Number(p.stock) || 0), 0),
  };

  const openAdd = () => { setForm(EMPTY_FORM); setModalOpen(true); };
  const openEdit = (p) => {
    setForm({
      id: p.id,
      name_ar: p.name_ar || '',
      name_en: p.name_en || '',
      category: p.category || 'supplements',
      price: p.price || '',
      stock: p.stock || '',
      status: p.status || 'active',
      description: p.description || '',
    });
    setModalOpen(true);
  };

  const saveProduct = async () => {
    if (!form.name_ar.trim() && !form.name_en.trim()) {
      toast.error(language === 'ar' ? 'يجب إدخال اسم المنتج (عربي أو إنجليزي)' : 'Product name required (AR or EN)');
      return;
    }
    if (form.price === '' || Number(form.price) < 0) {
      toast.error(language === 'ar' ? 'السعر غير صحيح' : 'Invalid price');
      return;
    }
    setSaving(true);
    const payload = {
      name_ar: form.name_ar,
      name_en: form.name_en,
      category: form.category,
      price: Number(form.price),
      stock: Number(form.stock || 0),
      status: form.status,
      description: form.description,
    };
    try {
      if (form.id) {
        await axios.put(`${API_URL}/api/fitness/products/${form.id}`, payload, { headers: getAuthHeaders() });
        toast.success(language === 'ar' ? 'تم تحديث المنتج' : 'Product updated');
      } else {
        await axios.post(`${API_URL}/api/fitness/products`, payload, { headers: getAuthHeaders() });
        toast.success(language === 'ar' ? 'تم إضافة المنتج' : 'Product added');
      }
      setModalOpen(false);
      fetchProducts();
    } catch (err) {
      toast.error(err.response?.data?.detail || (language === 'ar' ? 'فشل في الحفظ' : 'Failed to save'));
    } finally {
      setSaving(false);
    }
  };

  const deleteProduct = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/fitness/products/${id}`, { headers: getAuthHeaders() });
      toast.success(language === 'ar' ? 'تم حذف المنتج' : 'Product deleted');
      fetchProducts();
    } catch (err) {
      toast.error(err.response?.data?.detail || (language === 'ar' ? 'فشل في الحذف' : 'Failed to delete'));
    } finally {
      setConfirmDelete(null);
    }
  };

  return (
    <DashboardLayout
      title="إدارة المتجر"
      titleEn="Store Management"
      subtitle="إضافة وتتبع منتجات متجر 606 Fitness"
      subtitleEn="Add and track products inside the 606 Fitness store"
      screenCode="FIT-005"
      backPath="/dashboard/606-fitness"
      onBack={() => navigate('/dashboard/606-fitness')}
      breadcrumb={[
        { labelAr: '606 FITNESS', labelEn: '606 FITNESS', path: '/dashboard/606-fitness' },
        { labelAr: 'إدارة المتجر', labelEn: 'Store Management' },
      ]}
    >
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-testid="fitness-store-page">
        {/* Stats Strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <StatCard icon={Boxes}      color="emerald" label={language === 'ar' ? 'إجمالي المنتجات' : 'Total Products'} value={stats.total} />
          <StatCard icon={CheckCircle2} color="blue"  label={language === 'ar' ? 'منتجات نشطة'     : 'Active Products'}   value={stats.active} />
          <StatCard icon={AlertCircle} color="amber"  label={language === 'ar' ? 'نفذت الكمية'     : 'Out of Stock'}      value={stats.out_of_stock} />
          <StatCard icon={DollarSign}  color="gold"   label={language === 'ar' ? 'قيمة المخزون' : 'Inventory Value'} value={stats.inventory_value.toFixed(0)} />
        </div>

        {/* Search + Add */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#71717A]" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={language === 'ar' ? 'بحث بإسم المنتج...' : 'Search by product name...'}
              className="w-full bg-white/[0.02] border border-white/10 rounded-xl py-3 px-10 text-white placeholder-[#71717A] focus:outline-none focus:border-emerald-500/50"
              data-testid="store-search"
            />
          </div>
          <button
            onClick={fetchProducts}
            className="px-4 py-2 bg-emerald-500/10 border border-emerald-500/30 rounded-xl text-emerald-400 hover:bg-emerald-500/20 transition-colors"
            data-testid="store-refresh"
          >
            <RefreshCw className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={openAdd}
            className="px-5 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl font-medium flex items-center gap-2 transition-colors"
            data-testid="store-add-btn"
          >
            <Plus className="h-5 w-5" />
            {language === 'ar' ? 'إضافة منتج' : 'Add Product'}
          </button>
        </div>

        {/* Category Filter Chips */}
        <div className="flex flex-wrap gap-2 mb-6">
          {[{ value: 'all', ar: 'كل التصنيفات', en: 'All Categories' }, ...CATEGORIES].map((c) => (
            <button
              key={c.value}
              onClick={() => setCatFilter(c.value)}
              className={`px-4 py-2 rounded-xl border text-sm transition-all ${
                catFilter === c.value
                  ? 'bg-emerald-500/20 border-emerald-500/50 text-emerald-400'
                  : 'bg-white/[0.02] border-white/10 text-[#A1A1AA] hover:border-white/20'
              }`}
              data-testid={`store-cat-${c.value}`}
            >
              {language === 'ar' ? c.ar : c.en}
            </button>
          ))}
        </div>

        {/* Products Grid */}
        <div className="bg-white/[0.02] border border-white/10 rounded-2xl overflow-hidden">
          {loading ? (
            <div className="text-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-emerald-400 mx-auto" />
            </div>
          ) : visible.length === 0 ? (
            <div className="text-center py-16 text-[#A1A1AA]" data-testid="store-empty">
              <ShoppingBag className="h-14 w-14 mx-auto mb-3 opacity-30" />
              <p>{language === 'ar' ? 'لا توجد منتجات بعد' : 'No products yet'}</p>
              <p className="text-xs text-[#606060] mt-2">
                {language === 'ar' ? 'انقر على "إضافة منتج" لبدء بناء كتالوج المتجر' : 'Click "Add Product" to start building the store catalogue'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-4">
              {visible.map((p) => {
                const lowStock = Number(p.stock) <= 5;
                return (
                  <div
                    key={p.id}
                    className="bg-white/[0.02] border border-white/10 rounded-xl p-4 hover:border-emerald-500/30 transition-colors"
                    data-testid={`store-product-${p.id}`}
                  >
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div className="w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center shrink-0">
                        <Package className="h-5 w-5 text-emerald-400" />
                      </div>
                      <div className="flex gap-1">
                        <button
                          onClick={() => openEdit(p)}
                          className="p-1.5 rounded-lg bg-white/5 border border-white/10 text-[#A1A1AA] hover:text-emerald-400 hover:border-emerald-500/30 transition-colors"
                          data-testid={`store-edit-${p.id}`}
                          title={language === 'ar' ? 'تعديل' : 'Edit'}
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => setConfirmDelete(p)}
                          className="p-1.5 rounded-lg bg-white/5 border border-white/10 text-[#A1A1AA] hover:text-red-400 hover:border-red-500/30 transition-colors"
                          data-testid={`store-delete-${p.id}`}
                          title={language === 'ar' ? 'حذف' : 'Delete'}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                    <h3 className="text-white font-semibold truncate">
                      {language === 'ar' ? (p.name_ar || p.name_en) : (p.name_en || p.name_ar)}
                    </h3>
                    {p.description && (
                      <p className="text-xs text-[#A1A1AA] mt-1 line-clamp-2">{p.description}</p>
                    )}
                    <div className="flex items-center justify-between mt-3">
                      <span className="text-sm font-bold text-emerald-400">
                        {floorAED(p.price).toFixed(2)} <span className="text-xs text-[#A1A1AA]">AED</span>
                      </span>
                      <span className={`text-xs px-2 py-1 rounded-lg border ${
                        Number(p.stock) <= 0
                          ? 'bg-red-500/10 text-red-400 border-red-500/30'
                          : lowStock
                            ? 'bg-amber-500/10 text-amber-400 border-amber-500/30'
                            : 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30'
                      }`}>
                        {Number(p.stock) <= 0
                          ? (language === 'ar' ? 'نفذت' : 'Out')
                          : `${p.stock} ${language === 'ar' ? 'متوفر' : 'in stock'}`}
                      </span>
                    </div>
                    <div className="mt-2 text-[10px] text-[#71717A]">
                      {CATEGORIES.find((c) => c.value === p.category) ? (language === 'ar' ? CATEGORIES.find((c) => c.value === p.category).ar : CATEGORIES.find((c) => c.value === p.category).en) : p.category}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Add/Edit Modal */}
      <AnimatePresence>
        {modalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => !saving && setModalOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-[#0A0A0A] border border-white/10 rounded-2xl p-6 max-w-xl w-full"
              data-testid="store-product-modal"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-white">
                  {form.id
                    ? (language === 'ar' ? 'تعديل المنتج' : 'Edit Product')
                    : (language === 'ar' ? 'إضافة منتج جديد' : 'Add New Product')}
                </h3>
                <button onClick={() => setModalOpen(false)} className="p-1 text-[#71717A] hover:text-white">
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label={language === 'ar' ? 'الاسم (عربي)' : 'Name (Arabic)'}>
                  <input
                    type="text"
                    value={form.name_ar}
                    onChange={(e) => setForm({ ...form, name_ar: e.target.value })}
                    className="w-full bg-white/[0.02] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-emerald-500/50"
                    data-testid="store-form-name-ar"
                  />
                </Field>
                <Field label={language === 'ar' ? 'الاسم (إنجليزي)' : 'Name (English)'}>
                  <input
                    type="text"
                    value={form.name_en}
                    onChange={(e) => setForm({ ...form, name_en: e.target.value })}
                    className="w-full bg-white/[0.02] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-emerald-500/50"
                    data-testid="store-form-name-en"
                  />
                </Field>
                <Field label={language === 'ar' ? 'التصنيف' : 'Category'}>
                  <select
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-emerald-500/50"
                    data-testid="store-form-category"
                  >
                    {CATEGORIES.map((c) => (
                      <option key={c.value} value={c.value}>{language === 'ar' ? c.ar : c.en}</option>
                    ))}
                  </select>
                </Field>
                <Field label={language === 'ar' ? 'الحالة' : 'Status'}>
                  <select
                    value={form.status}
                    onChange={(e) => setForm({ ...form, status: e.target.value })}
                    className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-emerald-500/50"
                    data-testid="store-form-status"
                  >
                    <option value="active">{language === 'ar' ? 'نشط' : 'Active'}</option>
                    <option value="hidden">{language === 'ar' ? 'مخفي' : 'Hidden'}</option>
                  </select>
                </Field>
                <Field label={language === 'ar' ? 'السعر' : 'Price'}>
                  <input
                    type="number" min="0" step="0.01"
                    value={form.price}
                    onChange={(e) => setForm({ ...form, price: e.target.value })}
                    className="w-full bg-white/[0.02] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-emerald-500/50"
                    data-testid="store-form-price"
                  />
                </Field>
                <Field label={language === 'ar' ? 'الكمية' : 'Stock'}>
                  <input
                    type="number" min="0"
                    value={form.stock}
                    onChange={(e) => setForm({ ...form, stock: e.target.value })}
                    className="w-full bg-white/[0.02] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-emerald-500/50"
                    data-testid="store-form-stock"
                  />
                </Field>
                <div className="col-span-2">
                  <Field label={language === 'ar' ? 'الوصف' : 'Description'}>
                    <textarea
                      rows={3}
                      value={form.description}
                      onChange={(e) => setForm({ ...form, description: e.target.value })}
                      className="w-full bg-white/[0.02] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-emerald-500/50 resize-none"
                      data-testid="store-form-description"
                    />
                  </Field>
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setModalOpen(false)}
                  disabled={saving}
                  className="flex-1 py-3 rounded-xl bg-white/5 border border-white/10 text-[#A1A1AA] hover:bg-white/10 transition-colors disabled:opacity-50"
                >
                  {language === 'ar' ? 'إلغاء' : 'Cancel'}
                </button>
                <button
                  onClick={saveProduct}
                  disabled={saving}
                  className="flex-1 py-3 rounded-xl bg-emerald-500 text-white font-medium hover:bg-emerald-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="store-form-save"
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  {language === 'ar' ? 'حفظ' : 'Save'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete confirmation */}
      <AnimatePresence>
        {confirmDelete && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setConfirmDelete(null)}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-[#0A0A0A] border border-white/10 rounded-2xl p-6 max-w-md w-full"
              data-testid="store-delete-modal"
            >
              <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-red-500/20 flex items-center justify-center">
                <Trash2 className="h-7 w-7 text-red-400" />
              </div>
              <h3 className="text-lg font-bold text-white text-center mb-2">
                {language === 'ar' ? 'حذف المنتج' : 'Delete Product'}
              </h3>
              <p className="text-[#A1A1AA] text-center text-sm mb-5">
                {language === 'ar'
                  ? `هل أنت متأكد من حذف "${confirmDelete.name_ar || confirmDelete.name_en}"؟`
                  : `Are you sure you want to delete "${confirmDelete.name_en || confirmDelete.name_ar}"?`}
              </p>
              <div className="flex gap-3">
                <button onClick={() => setConfirmDelete(null)} className="flex-1 py-2.5 rounded-xl bg-white/5 border border-white/10 text-[#A1A1AA] hover:bg-white/10 transition-colors">
                  {language === 'ar' ? 'إلغاء' : 'Cancel'}
                </button>
                <button
                  onClick={() => deleteProduct(confirmDelete.id)}
                  className="flex-1 py-2.5 rounded-xl bg-red-500 text-white font-medium hover:bg-red-600 transition-colors"
                  data-testid="store-delete-confirm"
                >
                  {language === 'ar' ? 'حذف' : 'Delete'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </DashboardLayout>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs text-[#A1A1AA] mb-1">{label}</span>
      {children}
    </label>
  );
}

function StatCard({ icon: Icon, color, label, value }) {
  const palette = {
    emerald: { bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', text: 'text-emerald-400' },
    blue:    { bg: 'bg-blue-500/10',    border: 'border-blue-500/30',    text: 'text-blue-400' },
    amber:   { bg: 'bg-amber-500/10',   border: 'border-amber-500/30',   text: 'text-amber-400' },
    gold:    { bg: 'bg-[#d4af37]/10',   border: 'border-[#d4af37]/30',   text: 'text-[#d4af37]' },
  }[color] || { bg: 'bg-white/5', border: 'border-white/10', text: 'text-white' };
  return (
    <div className={`bg-white/[0.02] border border-white/10 rounded-2xl p-4 flex items-center gap-3`}>
      <div className={`w-11 h-11 rounded-xl ${palette.bg} ${palette.border} border flex items-center justify-center`}>
        <Icon className={`h-5 w-5 ${palette.text}`} />
      </div>
      <div>
        <p className="text-xs text-[#A1A1AA]">{label}</p>
        <p className="text-lg font-bold text-white">{value}</p>
      </div>
    </div>
  );
}
